import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getConfig, updateConfig } from '../../hooks/useAI';
import type { AIConfig } from '../../hooks/useAI';

const providers = [
  { value: 'vsends', label: 'VSENDS Built-in (Free)', description: 'Qwen 2.5 7B, self-hosted' },
  { value: 'openai', label: 'OpenAI', description: 'GPT-4o-mini default' },
  { value: 'anthropic', label: 'Anthropic', description: 'Claude Sonnet default' },
  { value: 'groq', label: 'Groq', description: 'Llama 3.3 70B, fast' },
  { value: 'together', label: 'TogetherAI', description: 'Llama 3.3 70B Turbo' },
  { value: 'openrouter', label: 'OpenRouter', description: 'Multi-model router' },
];

export default function AISettings() {
  const queryClient = useQueryClient();
  const { data: config, isLoading } = useQuery({
    queryKey: ['ai-config'],
    queryFn: getConfig,
  });

  const [form, setForm] = useState<Partial<AIConfig>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const updateMutation = useMutation({
    mutationFn: updateConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-config'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  const c = { ...config, ...form };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateMutation.mutateAsync(form);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const selectedProvider = providers.find((p) => p.value === c.provider) || providers[0];
  const needsKey = c.provider !== 'vsends';

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">AI Settings</h2>
        <p className="text-gray-500 mt-1">Configure your AI provider, model, and behavior settings.</p>
      </div>

      {saved && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-700">Settings saved successfully!</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Provider */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Provider</h3>
          <div className="space-y-3">
            {providers.map((p) => (
              <label
                key={p.value}
                className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  c.provider === p.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="provider"
                  value={p.value}
                  checked={c.provider === p.value}
                  onChange={() => updateField('provider', p.value)}
                  className="mt-1"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">{p.label}</p>
                  <p className="text-xs text-gray-500">{p.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* API Keys */}
        {needsKey && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">API Key</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {selectedProvider.label} API Key
              </label>
              <input
                type="password"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder={`Enter your ${selectedProvider.label} API key`}
                value={(form as any)[`${c.provider}_api_key`] || ''}
                onChange={(e) => updateField(`${c.provider}_api_key`, e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">Stored encrypted. Leave blank to keep existing key.</p>
            </div>
          </div>
        )}

        {/* Model Settings */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Model Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Model Name (optional)</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Leave blank for default"
                value={c.model_name || ''}
                onChange={(e) => updateField('model_name', e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Temperature: {c.temperature}
              </label>
              <input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={c.temperature}
                onChange={(e) => updateField('temperature', parseFloat(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Precise (0)</span>
                <span>Creative (2)</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Tokens</label>
              <input
                type="number"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={c.max_tokens}
                onChange={(e) => updateField('max_tokens', parseInt(e.target.value))}
              />
            </div>
          </div>
        </div>

        {/* Behavior */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Behavior</h3>
          <div className="space-y-4">
            <label className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Auto-execute actions</p>
                <p className="text-xs text-gray-500">AI executes actions without confirmation</p>
              </div>
              <input
                type="checkbox"
                checked={c.auto_execute}
                onChange={(e) => updateField('auto_execute', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded"
              />
            </label>

            <label className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Fallback to built-in model</p>
                <p className="text-xs text-gray-500">Use VSENDS AI if external provider fails</p>
              </div>
              <input
                type="checkbox"
                checked={c.use_builtin_if_no_key}
                onChange={(e) => updateField('use_builtin_if_no_key', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded"
              />
            </label>
          </div>
        </div>

        {/* Custom System Prompt */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Custom System Prompt</h3>
          <textarea
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            rows={6}
            placeholder="Leave blank to use the default VSENDS AI personality..."
            value={c.custom_system_prompt || ''}
            onChange={(e) => updateField('custom_system_prompt', e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">Override the default AI personality and behavior.</p>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </form>
    </div>
  );
}
